export type ItemImageBlobSize = "thumb" | "full";

/**
 * Which picture of an item to fetch. `version` is the item's current
 * `imageKey`.
 *
 * G-116: the image routes answer `private, max-age=300` at an address keyed by
 * the ITEM, so for five minutes after a photo changed — from the kiosk, the
 * CRM or an import — a browser could keep serving the old picture, and this
 * cache kept handing out the old blob to anyone still mounted. Putting the
 * key in the address (and in this cache's key) makes a new photo a new
 * address: nothing stale can match it, whoever changed it.
 */
export type ItemImageVariant = {
  size?: ItemImageBlobSize;
  version?: string | null;
};

/** The query string both image routes take. The server ignores `v`. */
export function itemImageQuery(variant?: ItemImageVariant): string {
  const params = new URLSearchParams();
  if (variant?.size === "thumb") params.set("size", "thumb");
  if (variant?.version) params.set("v", variant.version);
  const query = params.toString();
  return query ? `?${query}` : "";
}

type Entry = {
  consumers: number;
  /** Set when fetch succeeded. */
  url: string | null;
  /** In-flight fetch shared by concurrent acquires. */
  inflight: Promise<string | null> | null;
};

const store = new Map<string, Entry>();

function storeKey(
  itemId: string,
  size: ItemImageBlobSize,
  version: string | null | undefined,
): string {
  return `${size}\n${itemId}\n${version ?? ""}`;
}

function purgeIfIdle(k: string, e: Entry) {
  if (e.consumers > 0) return;
  if (e.url) {
    URL.revokeObjectURL(e.url);
  }
  store.delete(k);
}

/**
 * Reference-counted cache: same item + size + version shares one network fetch and one blob URL.
 * Each successful {@link acquireItemImageBlobUrl} must be paired with {@link releaseItemImageBlobUrl}
 * (typically in a `useEffect` cleanup).
 */
export function acquireItemImageBlobUrl(
  itemId: string,
  size: ItemImageBlobSize,
  version: string | null | undefined,
  fetcher: () => Promise<string | null>,
): Promise<string | null> {
  const k = storeKey(itemId, size, version);
  let e = store.get(k);
  if (!e) {
    e = { consumers: 0, url: null, inflight: null };
    store.set(k, e);
  }

  e.consumers += 1;

  if (e.url) {
    return Promise.resolve(e.url);
  }

  if (!e.inflight) {
    e.inflight = fetcher()
      .then((blobUrl) => {
        e!.inflight = null;
        if (blobUrl) {
          e!.url = blobUrl;
        }
        purgeIfIdle(k, e!);
        return blobUrl;
      })
      .catch((err: unknown) => {
        e!.inflight = null;
        purgeIfIdle(k, e!);
        throw err;
      });
  }

  return e.inflight;
}

export function releaseItemImageBlobUrl(
  itemId: string,
  size: ItemImageBlobSize,
  version: string | null | undefined,
): void {
  const k = storeKey(itemId, size, version);
  const e = store.get(k);
  if (!e) return;

  e.consumers -= 1;

  if (e.consumers > 0) {
    return;
  }

  if (e.url) {
    URL.revokeObjectURL(e.url);
    store.delete(k);
    return;
  }

  if (e.inflight) {
    void e.inflight.finally(() => {
      const cur = store.get(k);
      if (!cur || cur.consumers > 0) return;
      if (cur.url) {
        URL.revokeObjectURL(cur.url);
      }
      store.delete(k);
    });
    return;
  }

  store.delete(k);
}
